<template>
  <el-form label-position="top">
    <!-- Selection and Upload Row -->
    <div class="selection-row">
      <el-form-item v-if="payeesList.length > 0" label="Выбрать существующего получателя" :class="{ 'is-error': showErrors && !selectedPayee }" class="selection-item">
        <el-select v-model="selectedPayee" placeholder="Выбрать получателя">
          <el-option
            :key="'new'"
            label="Новый получатель"
            :value="null"
          />
          <el-option
            v-for="payee in payeesList"
            :key="payee.id"
            :label="payee.name"
            :value="payee"
          />
        </el-select>
      </el-form-item>
      
      <el-form-item class="selection-item">
        <div class="upload-container">
          <el-upload
            action=""
            :auto-upload="false"
            :on-change="handleFileChange"
            :before-upload="beforeUpload"
            :limit="1"
            :file-list="fileList"
            :on-remove="handleRemoveFile"
            ref="uploadRef"
          >
            <el-button type="primary">Загрузить файл с инвойсом</el-button>
            <template #tip>
              <div class="el-upload__tip">Загрузите файл с инвойсом - наш сервис автоматически заполнит все поля</div>
            </template>
          </el-upload>
          <div v-if="isProcessingFile" class="loading-overlay">
            <el-icon class="loading-icon"><Loading /></el-icon>
            <div class="loading-text">Файл обрабатывается</div>
          </div>
        </div>
      </el-form-item>
    </div>

    <el-form-item label="Наименование получателя" :class="{ 'is-error': showErrors && !name }">
      <el-input v-model="name" />
      <div v-if="showErrors && !name" class="el-form-item__error">Это поле обязательное</div>
    </el-form-item>
    <el-form-item label="Наименование получателя (англ.)">
      <el-input v-model="nameEn" />
      <div v-if="showErrors && !nameEn" class="el-form-item__error">Это поле обязательное</div>
    </el-form-item>
    <el-form-item label="Номер импортного контракта">
      <el-input v-model="importContractNumber" />
      <div v-if="showErrors && !importContractNumber" class="el-form-item__error">Это поле обязательное</div>
    </el-form-item>
    <el-form-item label="Номер инвойса">
      <el-input v-model="invoiceNumber" />
      <div v-if="showErrors && !invoiceNumber" class="el-form-item__error">Это поле обязательное</div>
    </el-form-item>
    <el-form-item label="Адрес получателя">
      <el-input v-model="address" />
      <div v-if="showErrors && !address" class="el-form-item__error">Это поле обязательное</div>
    </el-form-item>
    <el-form-item label="Адрес получателя (англ.)">
      <el-input v-model="addressEn" />
      <div v-if="showErrors && !addressEn" class="el-form-item__error">Это поле обязательное</div>
    </el-form-item>
    <el-form-item label="Расчетный счет получателя">
      <el-input v-model="account" />
      <div v-if="showErrors && !account" class="el-form-item__error">Это поле обязательное</div>
    </el-form-item>
    <el-form-item label="Наименование банка получателя">
      <el-input v-model="bankName" />
      <div v-if="showErrors && !bankName" class="el-form-item__error">Это поле обязательное</div>
    </el-form-item>
    <el-form-item label="Наименование банка получателя (англ.)">
      <el-input v-model="bankNameEn" />
      <div v-if="showErrors && !bankNameEn" class="el-form-item__error">Это поле обязательное</div>
    </el-form-item>
    <el-form-item label="Адрес банка получателя">
      <el-input v-model="bankAddress" />
      <div v-if="showErrors && !bankAddress" class="el-form-item__error">Это поле обязательное</div>
    </el-form-item>
    <el-form-item label="Адрес банка получателя (англ.)">
      <el-input v-model="bankAddressEn" />
      <div v-if="showErrors && !bankAddressEn" class="el-form-item__error">Это поле обязательное</div>
    </el-form-item>
    <el-form-item label="SWIFT получателя">
      <el-input v-model="bankSwift" />
      <div v-if="showErrors && !bankSwift" class="el-form-item__error">Это поле обязательное</div>
    </el-form-item>
  </el-form>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed } from 'vue'
import type { Payee } from '@/store/types'
import { useStore } from 'vuex'
import { Loading } from '@element-plus/icons-vue'
import type { UploadInstance, UploadFile } from 'element-plus'

export default defineComponent({
  name: 'PayeeInformation',
  components: {
    Loading
  },
  setup() {
    const store = useStore()
    const showErrors = ref(false)
    const isProcessingFile = ref(false)
    const fileList = ref<UploadFile[]>([])
    const uploadRef = ref<UploadInstance>()
    
    // Helper function to update payee fields
    const updatePayeeField = (field: string, value: any) => {
      const updatedPayee = { 
        ...(store.state.currentOrder?.payee || {}), 
        [field]: value 
      };
      store.commit('SET_PAYEE_FOR_CURRENT_ORDER', updatedPayee);
    }
    
    const payeesList = computed(() => store.state.payees)

    // Selected payee computed property
    const selectedPayee = computed({
      get: () => {
        const payeeId = store.state.currentOrder?.payee_id
        if (payeeId) {
          return store.state.payees.find((payee: Payee) => payee.id === payeeId) || null
        }
        return null  // This will select "Новый получатель" by default
      },
      set: (value: Payee | null) => {
        if (value) {
          store.commit('SET_PAYEE_FOR_CURRENT_ORDER', value)
          store.commit('UPDATE_ORDER_FIELD', { field: 'payee_id', value: value.id })
        } else {
          store.commit('UPDATE_ORDER_FIELD', { field: 'payee_id', value: null })
        }
      }
    })

    // Computed properties for each form field
    const name = computed({
      get: () => store.state.currentOrder?.payee?.name || '',
      set: (value) => updatePayeeField('name', value)
    })

    const nameEn = computed({
      get: () => store.state.currentOrder?.payee?.name_en || '',
      set: (value) => updatePayeeField('name_en', value)
    })

    const importContractNumber = computed({
      get: () => store.state.currentOrder?.payee?.import_contract_number || '',
      set: (value) => updatePayeeField('import_contract_number', value)
    })

    const invoiceNumber = computed({
      get: () => store.state.currentOrder?.payee?.invoice_number || '',
      set: (value) => updatePayeeField('invoice_number', value)
    })

    const address = computed({
      get: () => store.state.currentOrder?.payee?.address || '',
      set: (value) => updatePayeeField('address', value)
    })

    const addressEn = computed({
      get: () => store.state.currentOrder?.payee?.address_en || '',
      set: (value) => updatePayeeField('address_en', value)
    })

    const account = computed({
      get: () => store.state.currentOrder?.payee?.account || '',
      set: (value) => updatePayeeField('account', value)
    })

    const bankName = computed({
      get: () => store.state.currentOrder?.payee?.bank_name || '',
      set: (value) => updatePayeeField('bank_name', value)
    })

    const bankNameEn = computed({
      get: () => store.state.currentOrder?.payee?.bank_name_en || '',
      set: (value) => updatePayeeField('bank_name_en', value)
    })

    const bankAddress = computed({
      get: () => store.state.currentOrder?.payee?.bank_address || '',
      set: (value) => updatePayeeField('bank_address', value)
    })

    const bankAddressEn = computed({
      get: () => store.state.currentOrder?.payee?.bank_address_en || '',
      set: (value) => updatePayeeField('bank_address_en', value)
    })

    const bankSwift = computed({
      get: () => store.state.currentOrder?.payee?.bank_swift || '',
      set: (value) => updatePayeeField('bank_swift', value)
    })

    // Watch for validation - this one watch is still needed
    watch(() => store.getters.isPayeeInformationValid, (isValid) => {
      if (!isValid) {
        showErrors.value = true
      }
    })

    // Before upload handler to clear previous files
    const beforeUpload = (file: File) => {
      // Clear the file list before adding a new file
      fileList.value = []
      return true
    }

    // Handle file upload for automatic parsing
    const handleFileChange = (uploadFile: UploadFile) => {
      // Make sure we're only keeping the latest file
      fileList.value = [uploadFile]
      
      if (uploadFile && uploadFile.raw) {
        // Set loading state
        isProcessingFile.value = true
        
        // Send the file to the backend for processing
        store.dispatch('parsePayeeDocument', uploadFile.raw)
          .then(() => {
            isProcessingFile.value = false
          })
          .catch(error => {
            console.error('Error parsing document:', error)
            isProcessingFile.value = false
          })
      }
    }
    
    // Handle file removal
    const handleRemoveFile = () => {
      fileList.value = []
    }

    return {
      selectedPayee,
      showErrors,
      payeesList,
      isProcessingFile,
      fileList,
      uploadRef,
      handleFileChange,
      handleRemoveFile,
      beforeUpload,
      // Computed field properties
      name,
      nameEn,
      importContractNumber,
      invoiceNumber,
      address,
      addressEn,
      account,
      bankName,
      bankNameEn,
      bankAddress,
      bankAddressEn,
      bankSwift
    }
  }
})
</script>

<style scoped>
.selection-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.selection-item {
  flex: 1;
  min-width: 0;
}

.upload-container {
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.loading-icon {
  font-size: 24px;
  color: #409EFF;
  animation: rotating 2s linear infinite;
}

.loading-text {
  margin-top: 8px;
  color: #606266;
}

.el-upload__tip {
  line-height: 1.5;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style> 